import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { createCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPost } from '../../generated';
import type { CreateCollectionBody, Retailer } from '../../generated';
import type { CreateCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPostData } from '../../generated';
import type { CreateCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPostError } from '../../generated';
import type { CreateCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPostResponse } from '../../generated';

/**
 * The benchmark a collection scores against by default: exactly one of a custom benchmark id or a Vizit retailer.
 * Narrower than the generated `CollectionDefaultBenchmark`, where both keys are optional and nullable, so a caller
 * cannot send neither or both. It shadows the generated name in the package index.
 */
export type CollectionDefaultBenchmark = { benchmark_id: string } | { retailer: Retailer };

/** The generated `CreateCollectionBody` with `default_benchmark` narrowed to the union above. */
export type CreateCollectionRequestBody = Omit<CreateCollectionBody, 'default_benchmark'> & {
  default_benchmark?: CollectionDefaultBenchmark;
};

export type CreateCollectionMutationVariables =
  CreateCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPostData['path'] & CreateCollectionRequestBody;

export type CreateCollectionMutation = CreateCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPostResponse;

export const useCreateCollectionMutation = <
  TError = CreateCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPostError,
  TContext = unknown,
>(
  options?: Omit<
    UseMutationOptions<CreateCollectionMutation, TError, CreateCollectionMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<CreateCollectionMutation, TError, CreateCollectionMutationVariables, TContext>({
    mutationKey: ['CreateCollection'],
    mutationFn: async ({ workspace_id, ...body }: CreateCollectionMutationVariables) => {
      const { data } = await createCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPost({
        path: { workspace_id },
        body,
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useCreateCollectionMutation.fetcher = (
  { workspace_id, ...body }: CreateCollectionMutationVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await createCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsPost({
      path: { workspace_id },
      body,
      throwOnError: true,
    });
    return data;
  };
};
